import { useState, useEffect, useCallback } from 'react'
import { useMsal } from '@azure/msal-react'
import { apiTokenRequest } from '../auth/msalConfig'
import { createApi } from '../lib/api'
import type { AppRole, MeResponse, RoleGrant } from '../types'

export interface AuthUser {
  id: string
  name: string
  email: string
  /** Server-resolved roles, flattened out of `grants`. They stack, so read
   *  them through `isAdmin` / `isReportingAdmin` unless you need the exact list. */
  roles: AppRole[]
  grants: RoleGrant[]
  /** PlatformAdmin: Settings > Admin, the audit log, and every exemption. */
  isAdmin: boolean
  /** PlatformAdmin or StatusReportingAdmin: sets reporting windows, grants
   *  extensions and edits the board date rules. */
  isReportingAdmin: boolean
  /** PlatformAdmin or SurveyAdmin: sets up and sends surveys (Settings > Surveys). */
  canManageSurveys: boolean
  /** Any of those or SurveyResults: reads survey answers in Reports. */
  canReadSurveyResults: boolean
}

// Local visual-test mode: VITE_USE_MOCK_API=true also bypasses MSAL so you
// can click through the UI without signing in. Production builds ignore this.
const USE_MOCK = import.meta.env.VITE_USE_MOCK_API === 'true'

const unscoped = (roles: AppRole[]): RoleGrant[] => roles.map(role => ({ role, scopes: [] }))

/** Attach the roles and the derived booleans to an identity. PlatformAdmin
 *  implies every other role; SurveyAdmin implies SurveyResults. */
function withGrants(
  identity: { id: string; name: string; email: string },
  grants: RoleGrant[],
): AuthUser {
  const roles = [...new Set(grants.map(grant => grant.role))]
  const isAdmin = roles.includes('PlatformAdmin')
  return {
    ...identity,
    roles,
    grants,
    isAdmin,
    isReportingAdmin: isAdmin || roles.includes('StatusReportingAdmin'),
    canManageSurveys: isAdmin || roles.includes('SurveyAdmin'),
    canReadSurveyResults: isAdmin || roles.includes('SurveyAdmin') || roles.includes('SurveyResults'),
  }
}

const MOCK_USER: AuthUser = withGrants(
  { id: 'mock-self-oid', name: 'Local Tester', email: 'local.tester@danone.com' },
  unscoped(['PlatformAdmin', 'User']),
)

// The route guard, the layout and several pages all mount useAuth on the same
// page load, so one in-flight `GET /me` is shared between them. A failure
// clears the cache, so the next mount retries rather than caching the miss.
let grantsCache: { oid: string; grants: Promise<RoleGrant[]> } | null = null

/**
 * `GET /me` answers with `grants` and the flat `roles`. Both are read: a role
 * the server resolves without a stored grant (the Entra admin group, or the
 * legacy reporting window managers list before it is carried across) may only
 * appear in `roles`.
 */
function grantsFromMe(me: MeResponse | null): RoleGrant[] {
  const grants = (me?.grants || []).map(grant => ({ role: grant.role, scopes: [...(grant.scopes || [])] }))
  for (const role of me?.roles || []) {
    if (!grants.some(grant => grant.role === role)) grants.push({ role, scopes: [] })
  }
  return grants.length ? grants : unscoped(['User'])
}

/**
 * Ask the backend what the caller holds. Authorization is resolved
 * server-side (Entra group membership as break-glass, unioned with the grants
 * stored on the user document), never from the ID token's `groups` claim.
 *
 * A failure resolves to `User` rather than rejecting: an authenticated user
 * whose grants could not be read still gets the app, just without the
 * privileged surfaces. The backend enforces the real check either way.
 */
function resolveGrants(oid: string, getToken: () => Promise<string | null>): Promise<RoleGrant[]> {
  if (grantsCache?.oid === oid) return grantsCache.grants
  const grants = createApi(getToken)
    .getMe()
    .then(grantsFromMe)
    .catch((err: unknown) => {
      console.warn('Could not resolve roles from /me - treating this user as User.', err)
      grantsCache = null
      return unscoped(['User'])
    })
  grantsCache = { oid, grants }
  return grants
}

/** Acquire a bearer token for backend API calls. */
export function useAccessToken() {
  const { instance, accounts } = useMsal()

  const getToken = useCallback(async (): Promise<string | null> => {
    if (USE_MOCK) return 'mock-token'
    const account = accounts[0]
    if (!account) return null
    try {
      const resp = await instance.acquireTokenSilent({ ...apiTokenRequest, account })
      return resp.accessToken
    } catch {
      // If silent fails, fall back to popup
      try {
        const resp = await instance.acquireTokenPopup(apiTokenRequest)
        return resp.accessToken
      } catch {
        return null
      }
    }
  }, [instance, accounts])

  return getToken
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(USE_MOCK ? MOCK_USER : null)
  const [loading, setLoading] = useState(!USE_MOCK)
  const { instance, accounts } = useMsal()
  const getToken = useAccessToken()

  useEffect(() => {
    if (USE_MOCK) return
    const account = accounts[0]
    if (!account) {
      setUser(null)
      setLoading(false)
      return
    }
    // Stay loading until the grants land, so the pages that redirect on
    // `!isAdmin` don't bounce an admin during the round trip.
    let active = true
    setLoading(true)
    resolveGrants(account.localAccountId, getToken).then(grants => {
      if (!active) return
      setUser(withGrants({
        id: account.localAccountId,
        name: account.name ?? account.username,
        email: account.username,
      }, grants))
      setLoading(false)
    })
    return () => { active = false }
  }, [accounts, getToken])

  const logout = useCallback(() => {
    if (USE_MOCK) return
    grantsCache = null
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin })
  }, [instance])

  const login = useCallback(() => {
    if (USE_MOCK) return
    instance.loginRedirect({ scopes: ['User.Read', 'openid', 'profile', 'email'] })
  }, [instance])

  return { user, loading, logout, login }
}
